import {
  ChangeDetectorRef,
  Component,
  computed,
  OnInit,
  signal,
  Signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommunicationService } from '../../services/communication.service';
import { QuotaManagementService } from '../../services/quota-management.service';
import { MessageService } from '../../services/message.service';

@Component({
    selector: 'manage-quotas',
    templateUrl: './manage-quotas.component.html',
    styleUrl: './manage-quotas.component.css',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ManageQuotasComponent implements OnInit {
  isSuperUser: boolean;
  isParentOrgSuperUser = false;
  featureName: string = 'Magic Insights'; // The feature we are managing quotas for
  selectedQuotaType: 'MONTHLY' | 'BULK' = 'MONTHLY'; // The type of quota pool (monthly or bulk)
  organisationQuotaDetails: Record<string, any>;
  teamQuotaDetails: Record<string, any>;
  teamQuotaCache: Record<string, any> = {};
  userQuotaDetails: Record<string, any>; // For non-superusers
  orgQuotaAllocated: WritableSignal<number> = signal(0);
  orgRemainingQuota: Signal<number> = signal(0);

  loadingStatus: any;

  constructor(
    private route: ActivatedRoute,
    private communicationService: CommunicationService,
    private quotaManagementService: QuotaManagementService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    document.title = 'Manage Quota';
    const queryParams = this.route.snapshot.queryParams;
    this.featureName = queryParams['type'] || 'Magic Insights';
    this.loadingStatus = this.quotaManagementService.isLoading;
    this.communicationService.getIsSuperUserMessage().subscribe(isSuperUser => {
      this.isSuperUser = isSuperUser;
    });
    this.communicationService
      .getIsParentOrgSuperUserMessage()
      .subscribe(isParentOrgSuperUser => {
        this.isParentOrgSuperUser = isParentOrgSuperUser;
        this.loadInitialData();
      });
  }

  loadInitialData(): void {
    if (this.isParentOrgSuperUser) {
        this.getOrgQuotaDetails();
    } else if (this.isSuperUser) {
        this.getTeamDetails('');
    }  else {
        this.getUserQuotaDetails();
    }
  }

  onQuotaTypeChange(): void {
    this.teamQuotaCache = {}; // Clear cache when type changes
    this.organisationQuotaDetails = null;
    this.teamQuotaDetails = null;
    this.userQuotaDetails = null;
    this.loadInitialData();
  }

  getOrgQuotaDetails() {
    this.quotaManagementService
      .getOrganisationQuotaDetails(this.featureName, this.selectedQuotaType)
      .subscribe(data => {
        if (data) {
          this.organisationQuotaDetails = data;
          this.orgQuotaAllocated.set(
            data.teams.reduce((total, team) => (total += team.quota), 0)
          );
          this.orgRemainingQuota = computed(
            () =>
              this.organisationQuotaDetails.organisation_quota -
              this.orgQuotaAllocated()
          );
        }
      });
  }

  getTeamDetails(teamId: number | string, reFetch = false): void {
    const cacheKey = `${this.selectedQuotaType}_${teamId}`;
    if (!reFetch && this.teamQuotaCache[cacheKey]) {
      this.teamQuotaDetails = this.teamQuotaCache[cacheKey];
      return;
    }
    this.quotaManagementService
      .getTeamQuotaDetails(this.featureName, this.selectedQuotaType, teamId)
      .subscribe(data => {
        if (data) {
          this.teamQuotaDetails = data;
          this.teamQuotaDetails['currentTeamTotal'] = data.users.reduce(
            (count, user) => (count += user.quota),
            0
          );
          this.teamQuotaCache[cacheKey] = this.teamQuotaDetails;
          this.cdr.detectChanges();
        }
      });
  }

  getUserQuotaDetails(): void {
    this.quotaManagementService
      .getUserQuotaDetails(this.featureName, this.selectedQuotaType)
      .subscribe(data => {
        if (data) {
          this.userQuotaDetails = data;
        }
      });
  }

  setQuota(
    quota: number,
    userId?: number,
    teamId?: number
  ): void {
    if (quota < 0) {
      this.messageService.showAndHideAfterDelay('Please enter a positive integer', 3100);
      return;
    }

    this.quotaManagementService
      .setQuota(this.featureName, this.selectedQuotaType, quota, userId, teamId)
      .subscribe(data => {
        if (data.success) {
          this.messageService.showInfo('Quota updated successfully', 4000);
          // Refresh data after update
          if (teamId && !userId) { // it was a team update
             this.getOrgQuotaDetails(); // Refresh the whole org view
          }
          this.getTeamDetails(teamId || '', true);
        }
      });
  }

  equallyDistributeQuotas(): void {
    this.quotaManagementService
      .equallyDistributeQuotas(this.featureName, this.selectedQuotaType)
      .subscribe(data => {
        if (data.success) {
          this.messageService.showInfo('Quotas Equally Distributed.', 4000);
          this.getOrgQuotaDetails();
          // Clear team details to force a refresh on next expansion
          this.teamQuotaDetails = null;
          this.teamQuotaCache = {};
        }
      });
  }

  getProgressBarValue(currentTotal: number, assignedTotal: number): number {
    if (assignedTotal === 0) return 0;
    return (currentTotal / assignedTotal) * 100;
  }
}
